import type { ComponentType, SVGProps } from 'react';
import type { Permission } from '@immo/api-contract';
import { ChatIcon, ClockIcon, InvestmentIcon, MapIcon, ServerIcon, ShieldIcon, TagIcon, WorkspacesIcon } from './icons';

export type NavItem = {
  readonly path: string;
  readonly labelKey: string;
  readonly icon: ComponentType<SVGProps<SVGSVGElement>>;
  readonly children?: ReadonlyArray<NavChildItem>;
  /** When set, the whole group is hidden unless the admin holds this permission. */
  readonly permission?: Permission;
  /** Hidden on the hosted sandbox's admin app (`VITE_SANDBOX`), whatever the admin holds. */
  readonly productionOnly?: true;
};

export type NavChildItem = {
  readonly path: string;
  readonly labelKey: string;
  /**
   * When set, the child is hidden unless the admin holds this permission, and a
   * group left with no visible child hides too. Only the Logs children declare
   * one (IMM-494.1) — every other child is permission-free, so every other
   * group's visibility is exactly what it was before per-child gating existed.
   */
  readonly permission?: Permission;
};

export const navItems: ReadonlyArray<NavItem> = [
  {
    path: '/workspaces',
    labelKey: 'workspaces',
    icon: WorkspacesIcon,
    children: [
      { path: '/workspaces/members', labelKey: 'members' },
      { path: '/workspaces/organizations', labelKey: 'organizations' },
      { path: '/workspaces/properties', labelKey: 'properties', permission: 'backoffice:property:property-read' },
      { path: '/workspaces/deals/deleted', labelKey: 'deleted-deals', permission: 'backoffice:deal:deal-read' },
      {
        path: '/workspaces/companies/deleted',
        labelKey: 'deleted-companies',
        permission: 'backoffice:relationship:company-read',
      },
      { path: '/workspaces/api-keys', labelKey: 'api-keys' },
    ],
  },
  {
    // The cross-tenant operator queue (IMM-407.4) and the valuation agent's token (IMM-1402). The nav path is
    // the queue route; its detail route lives under it but is not a nav child. Nav path ≠ page file path in
    // this app. The agent page is `/valuation-agent`, never `/valuations/agent`, so `/valuations/:id` cannot
    // shadow it.
    path: '/valuations',
    labelKey: 'valuations',
    icon: InvestmentIcon,
    children: [
      { path: '/valuations', labelKey: 'valuations' },
      {
        path: '/valuation-agent',
        labelKey: 'valuation-agent',
        permission: 'backoffice:identity:valuation-agent-token-read',
      },
    ],
  },
  {
    // Operator read of members' messenger chats (WhatsApp, Telegram) across all
    // workspaces (IMM-1040.6, IMM-1127.1). Read-only, gated by the same permission as
    // IMM-928.4's WhatsApp reads — no dedicated permission was added.
    path: '/messenger/chats',
    labelKey: 'messenger-chats',
    icon: ChatIcon,
    permission: 'backoffice:communications:whatsapp-dispatch-read',
  },
  {
    // The operator-facing logs (IMM-494.1). A container, not a page — its nav
    // path is its first child, as the Skills group does. Each child carries its
    // own read permission, so an operator sees whichever logs they may read and
    // the group disappears when that is none. `/logs/api-key-requests` and its
    // child arrive together in IMM-494.2, so no entry here ever points at an
    // unregistered route.
    path: '/logs/api-calls',
    labelKey: 'logs',
    icon: ClockIcon,
    children: [
      { path: '/logs/api-calls', labelKey: 'api-calls', permission: 'backoffice:identity:api-call-read' },
      {
        path: '/logs/api-key-requests',
        labelKey: 'api-key-requests',
        permission: 'backoffice:identity:api-key-read',
      },
      { path: '/logs/emails', labelKey: 'emails', permission: 'backoffice:identity:audit-event-read' },
    ],
  },
  {
    // The product-managed Skills surface (IMM-406). The whole group is gated on
    // `backoffice:product-skill:read` — hidden for operators without it, whose
    // direct-URL hits are refused by the route loader's 403 → access-denied.
    path: '/admin/skills/document-analyses',
    labelKey: 'skills',
    icon: TagIcon,
    permission: 'backoffice:product-skill:read',
    children: [
      { path: '/admin/skills/document-analyses', labelKey: 'document-analyses' },
      { path: '/admin/skills/agent-skills', labelKey: 'agent-skills' },
      { path: '/admin/skills/fact-keys', labelKey: 'fact-keys' },
    ],
  },
  {
    path: '/admin',
    labelKey: 'admin',
    icon: ShieldIcon,
    children: [
      { path: '/admins', labelKey: 'admins' },
      // Hidden entirely for an operator without the read grant, matching every
      // other permission-gated entry; a direct URL still lands on the
      // access-denied screen via the loader's 403.
      { path: '/admin/mcp-access', labelKey: 'mcp-access', permission: 'backoffice:identity:mcp-access-read' },
    ],
  },
  {
    // The agent fleet (IMM-910.7, IMM-938). Hidden wholesale without the read
    // grant, matching the Skills group precedent. The board holds every job:
    // a worker wedged on a job it never closed shows there, not on machines.
    path: '/fleet',
    labelKey: 'fleet',
    icon: ServerIcon,
    permission: 'backoffice:fleet:read',
    children: [
      { path: '/fleet', labelKey: 'machines' },
      { path: '/fleet/board', labelKey: 'board' },
      { path: '/fleet/metrics', labelKey: 'metrics' },
      { path: '/fleet/accounts', labelKey: 'fleet-accounts' },
    ],
  },
  {
    // LandIQ's staging data runs (IMM-1132). A group so later LandIQ pages have a home; the nav
    // path is its first child, like the Skills group.
    path: '/landiq/data-runs',
    labelKey: 'landiq',
    icon: MapIcon,
    permission: 'backoffice:admin:landiq-data-run-manage',
    children: [{ path: '/landiq/data-runs', labelKey: 'data-runs' }],
  },
  {
    // The sandbox's data reset (IMM-1476). Top level: Fleet is gated by `fleet:read`, this page needs `command`.
    path: '/sandbox',
    labelKey: 'sandbox',
    icon: ServerIcon,
    permission: 'backoffice:fleet:command',
    productionOnly: true,
  },
];
